import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Quote, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import blogTestimonial1 from '@/assets/blog-articles-testimonial-1.jpg';
import blogTestimonial2 from '@/assets/blog-articles-testimonial-2.jpg';
import blogTestimonial3 from '@/assets/blog-articles-testimonial-3.jpg';

const BlogArticlesTestimonialsSection = () => {
  const reduce = useReducedMotion();
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      quote: "Our blog traffic increased 40% in just 3 months. gimmefy's AI creates content that actually ranks and converts.",
      author: "David Mitchell",
      role: "Content Marketing Director",
      image: blogTestimonial1
    },
    {
      quote: "From blank page to published article in under an hour. The quality is consistently professional—better than most freelancers.",
      author: "Jennifer Liu",
      role: "Digital Marketing Manager",
      image: blogTestimonial2
    },
    {
      quote: "Finally, an AI that understands my voice and brand. Every article sounds authentic while saving me hours of work.",
      author: "Fatima Al-Zahra",
      role: "Content Strategist",
      image: blogTestimonial3
    }
  ];

  // Auto-scroll every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-gradient-subtle relative overflow-hidden">
      {/* Background decorative elements - Responsive */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-16 sm:w-24 md:w-32 h-16 sm:h-24 md:h-32 bg-primary/20 rounded-full blur-lg sm:blur-xl"></div>
        <div className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-20 sm:w-32 md:w-40 h-20 sm:h-32 md:h-40 bg-accent/20 rounded-full blur-lg sm:blur-xl"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 bg-gradient-primary bg-clip-text text-transparent">
            What our users say
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            See how gimmefy helps teams create professional blog content faster and more effectively
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Main testimonial card - Mobile optimized */}
          <motion.div
            key={currentIndex}
            initial={reduce ? undefined : { opacity: 0, y: 20 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 active:scale-[0.98] touch-manipulation"
          >
            <div className="grid gap-4 sm:gap-6 lg:grid-cols-[auto_1fr] items-center">
              {/* Author Image with enhanced styling - Mobile first */}
              <div className="flex justify-center lg:justify-start order-1 lg:order-1">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-primary rounded-xl sm:rounded-2xl blur opacity-20 sm:opacity-25 group-hover:opacity-40 transition-opacity"></div>
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 rounded-xl sm:rounded-2xl overflow-hidden border-4 border-white/20 shadow-lg sm:shadow-2xl">
                    <img
                      src={currentTestimonial.image}
                      alt={`${currentTestimonial.author}, ${currentTestimonial.role}`}
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      loading="eager"
                      decoding="async"
                      sizes="(max-width: 640px) 128px, (max-width: 768px) 160px, (max-width: 1024px) 192px, 224px"
                    />
                  </div>
                </div>
              </div>

              {/* Testimonial Content with better styling - Mobile optimized */}
              <div className="space-y-3 sm:space-y-4 order-2 lg:order-2 text-center lg:text-left">
                <div className="relative">
                  <Quote className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-primary/30 absolute -top-1 sm:-top-2 -left-1 sm:-left-2" />
                  <blockquote className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground leading-relaxed pl-4 sm:pl-6 relative z-10">
                    "{currentTestimonial.quote}"
                  </blockquote>
                </div>
                
                <div className="space-y-1 sm:space-y-2 pl-4 sm:pl-6">
                  <div className="text-lg sm:text-xl font-bold text-foreground">
                    {currentTestimonial.author}
                  </div>
                  <div className="text-sm sm:text-base text-primary font-medium">
                    {currentTestimonial.role}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Indicators - Touch-friendly */}
          <div className="flex justify-center mt-6 sm:mt-8 space-x-3 sm:space-x-4">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative transition-all duration-300 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center ${
                  index === currentIndex 
                    ? 'w-12 h-4 bg-primary rounded-full' 
                    : 'w-4 h-4 bg-muted-foreground/40 hover:bg-muted-foreground/60 active:bg-muted-foreground/70 rounded-full hover:scale-110 active:scale-95'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              >
                <span className={`${
                  index === currentIndex 
                    ? 'w-12 h-4 bg-primary rounded-full' 
                    : 'w-4 h-4 bg-muted-foreground/40 rounded-full'
                }`} />
              </button>
            ))}
          </div>

          {/* CTA Section - Mobile optimized */}
          <motion.div 
            initial={reduce ? undefined : { opacity: 0, y: 30 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-8 sm:mt-12 space-y-3 sm:space-y-4"
          >
            <div className="max-w-lg mx-auto">
              <Button 
                variant="hero" 
                size="lg" 
                className="text-base sm:text-lg px-8 sm:px-12 py-4 sm:py-6 group w-full sm:w-auto min-h-[48px] active:scale-95"
                onClick={() => window.location.href = '/signup/contentcal'}
              >
                Discover for FREE
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              
              <p className="text-muted-foreground mt-2 sm:mt-3 text-sm sm:text-base">
                No credit card required • Start building today
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BlogArticlesTestimonialsSection;